import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { registerToastAction } from '../../../../redux/slices/toasts/toastActions';
import { ToastType } from '../../../../redux/slices/toasts/state';
import type { PeopleRow } from '../outreachTypes';
import { peopleMessages as messages } from '../live/peopleMessages';
import { useBlocks, type AddBlockInput } from './useBlocks';
import { useInvalidateOutreach } from './useInvalidateOutreach';
import { usePeopleActions } from './usePeopleActions';

/**
 * The actions of the block menu of a People row (design outreach-v2 g2), each with its toast. A
 * block offers Undo (`blocks.remove`) while its toast shows. Every action works on a sent row only: a scheduled
 * row has no uuid that names the person. The toast hides by the toast timer, not after exactly 10 s.
 */
export function useBlockActions(accountId: number, campaignUuid: string) {
  const intl = useIntl();
  const { show, showSuccess, showError } = useShowToast();
  const invalidate = useInvalidateOutreach();
  const { addBlock, removeBlock } = useBlocks(accountId);
  const people = usePeopleActions(accountId, campaignUuid);
  const removers = useRef<Array<() => void>>([]);
  useEffect(() => {
    const list = removers.current;
    return () => list.forEach((remove) => remove());
  }, []);

  const failed = useCallback(
    (error: unknown) => {
      console.error(error);
      showError(intl.formatMessage(messages.actionFailed));
    },
    [intl, showError]
  );

  const block = useCallback(
    (input: AddBlockInput, name: string) => {
      addBlock(input)
        .then((answer) => {
          invalidate(['campaigns']);
          if (answer.status !== 'added') {
            return;
          }
          const actionId = `outreach.block.undo.${answer.block.blockUuid}`;
          removers.current.push(
            registerToastAction(actionId, () => {
              removeBlock(answer.block.blockUuid)
                .then(() => invalidate(['campaigns']))
                .catch(failed);
            })
          );
          show({
            type: ToastType.success,
            uniqueId: actionId,
            description:
              answer.cancelled > 0
                ? intl.formatMessage(messages.blockedCancelled, { name, count: answer.cancelled })
                : intl.formatMessage(messages.blocked, { name }),
            autoHide: true,
            action: { label: intl.formatMessage(messages.undo), actionId },
          });
        })
        .catch(failed);
    },
    [addBlock, failed, intl, invalidate, removeBlock, show]
  );

  const blockPerson = useCallback(
    (row: PeopleRow, name: string) => {
      if (row.workspaceContactUuid !== null) {
        block({ kind: 'contact', workspaceContactUuid: row.workspaceContactUuid }, name);
      }
    },
    [block]
  );
  const blockTarget = useCallback(
    (row: PeopleRow) => {
      if (row.targetUuid !== null) {
        block({ kind: 'playlist', targetUuid: row.targetUuid }, row.name ?? '');
      }
    },
    [block]
  );
  const fewer = useCallback(
    (row: PeopleRow) => {
      people
        .dislike(row.messageUuid)
        .then(() => showSuccess(intl.formatMessage(messages.fewerDone, { name: row.name ?? '' })))
        .catch(failed);
    },
    [failed, intl, people, showSuccess]
  );
  const stopFollowUp = useCallback(
    (messageUuid: string) => {
      people
        .stopFollowUp(messageUuid)
        .then(() => showSuccess(intl.formatMessage(messages.followUpStopped)))
        .catch(failed);
    },
    [failed, intl, people, showSuccess]
  );

  return { blockPerson, blockTarget, fewer, stopFollowUp, isWriting: people.isWriting };
}
